import { useState } from "react";
import { useSupporter } from "@/context/SupporterContext";
import { Coffee, Heart, CheckCircle2, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";

export function ProSupporterDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { isProSupporter, setIsProSupporter } = useSupporter();
  const [showConfirm, setShowConfirm] = useState(false);

  if (!isOpen) return null;

  const handleSupportClick = () => {
    window.open("https://ko-fi.com/rahil1202", "_blank");
    setTimeout(() => {
      setShowConfirm(true);
    }, 2000);
  };

  const handleConfirm = () => {
    setIsProSupporter(true);
    toast.success("Thank you for your support! 💛", {
      description: "Pro Supporter theme unlocked globally.",
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <button 
          onClick={onClose}
          className="absolute right-4 top-4 text-text-secondary hover:text-foreground"
        >
          ✕
        </button>

        <div className="flex flex-col items-center text-center space-y-4">
          <div className="h-16 w-16 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 mb-2">
            <Heart className="h-8 w-8 fill-current" />
          </div>
          
          <h2 className="font-serifAccent text-2xl font-bold">Support PDFWorkSpace</h2>
          
          <p className="text-sm text-text-secondary">
            This tool is 100% free, runs entirely in your browser, and has zero ads. If it saves you time, consider buying the creator a coffee!
          </p>

          <div className="w-full space-y-3 py-4 text-left">
            <div className="flex items-center gap-3 text-sm">
              <ShieldCheck className="h-5 w-5 text-green-500" />
              <span>Keeps the project private and serverless</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Zap className="h-5 w-5 text-amber-500" />
              <span>Unlocks the exclusive <strong>Gold Supporter Theme</strong></span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <CheckCircle2 className="h-5 w-5 text-blue-500" />
              <span>Adds a Pro Badge to your sidebar</span>
            </div>
          </div>

          {!showConfirm && !isProSupporter && (
            <button
              onClick={handleSupportClick}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-3 font-semibold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-95"
            >
              <Coffee className="h-5 w-5" />
              Support on Ko-fi
            </button>
          )}

          {showConfirm && !isProSupporter && (
            <div className="w-full space-y-3 animate-in fade-in slide-in-from-bottom-2">
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Did you buy a coffee?</p>
              <button
                onClick={handleConfirm}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-foreground px-4 py-3 font-semibold text-background shadow-lg transition-transform hover:scale-[1.02] active:scale-95"
              >
                <Heart className="h-5 w-5" />
                Yes, I supported! (Unlock Theme)
              </button>
            </div>
          )}

          {isProSupporter && (
            <div className="w-full rounded-xl bg-amber-500/10 border border-amber-500/20 p-4 text-amber-600 dark:text-amber-400 font-medium">
              You are already a Pro Supporter. Thank you! 💛
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
